import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import ModalActions from 'reducers/Modal'
import { thunks as AuthThunks } from 'reducers/Auth'
import { Button, Modal, Tonality, Banner } from '@dsny/dsny-component-library'
import { Close } from '@dsny/dsny-component-library/icons'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { trackEvent } from 'utils/segmentService'
import InviteState from './InviteState'
import {
  XButtonWrapper,
  ButtonWrapper,
  ListTitleWrapper,
  ModalWrapper,
  InputWrapper,
  TextFieldDropdownWrapper
} from './InviteConnection.styles'

const InviteList = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()

  const [isCancel, setIsCancel] = useState(false)
  const [error, setError] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [newEntry, setNewEntry] = useState({
    textFieldValue: '',
    dropDownValue: ''
  })
  const [errorsState, setErrorsState] = useState({
    textField: '',
    dropDown: ''
  })

  const showInviteListModal = useSelector(
    (state) => state.modal?.showInviteListModal
  )
  const showInviteListStateModal = useSelector(
    (state) => state.modal?.showInviteListStateModal
  )

  const options = [
    { description: t('invite-connection.desc-coworker'), value: 'coworker' },
    {
      description: t('invite-connection.desc-industry-contact'),
      value: 'industry-contact'
    },
    { description: t('invite-connection.desc-other'), value: 'other' }
  ]

  const trackInviteListClick = (type, properties = {}) => {
    trackEvent('Click', {
      source: 'invite_list',
      type,
      ...properties
    })
  }

  const handleListCancelButtonClick = () => {
    trackInviteListClick('close')
    dispatch(ModalActions.toggleInviteListModal())
    setIsCancel(true)
  }

  useEffect(() => {
    if (isSubmitted) {
      validateFields(true)
    }
  }, [newEntry, isSubmitted])

  const validateFields = (shouldShowErrors) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const validDropdownValues = options.map((option) => option.description)

    const errors = {
      textField:
        !newEntry.textFieldValue || !emailRegex.test(newEntry.textFieldValue)
          ? t('invite-connection.email-error-message')
          : '',
      dropDown:
        !newEntry.dropDownValue ||
        !validDropdownValues.includes(newEntry.dropDownValue)
          ? t('invite-connection.dropdown-error-message')
          : ''
    }

    if (shouldShowErrors) {
      setErrorsState(errors)
    }
    return !errors.textField && !errors.dropDown
  }

  const handleFieldChange = (field, value) => {
    setNewEntry((prev) => ({ ...prev, [field]: value }))
  }

  const handleInvite = async () => {
    setIsSubmitted(true)
    const isValid = validateFields(true)

    if (!isValid) {
      return
    }

    const selectedOption = options.find(
      (option) => option.description === newEntry.dropDownValue
    )

    const invitations = [
      {
        invitee_email: newEntry.textFieldValue.toLowerCase(),
        relationship: selectedOption ? selectedOption.value : ''
      }
    ]
    const relationship = invitations[0].relationship

    try {
      trackInviteListClick('submit_invite', { relationship })
      await dispatch(AuthThunks.sendInvitations({ invitations }))
      dispatch(ModalActions.toggleInviteListStateModal())
      setIsCancel(false)
      setError(false)
      setNewEntry({
        textFieldValue: '',
        dropDownValue: ''
      })
      setErrorsState({ textField: '', dropDown: '' })
      setIsSubmitted(false)
    } catch (inviteError) {
      trackInviteListClick('submit_invite_error', { relationship })
      setError(true)
      console.error(inviteError?.data)
    }
  }

  return (
    <>
      <Modal
        isOpen={showInviteListModal}
        analyticsModalType="invite_list"
        trackEvent={trackEvent}
        containerStyles={{
          padding: isMobile ? '24px 12px' : '24px 64px 48px 64px',
          background: theme.colors.neutralW20,
          border: `1px solid ${theme.colors.neutralW30}`,
          width: isMobile ? '100%' : 676
        }}
        onClose={handleListCancelButtonClick}>
        <XButtonWrapper
          onClick={() => {
            handleListCancelButtonClick()
          }}>
          <Close width={14} color={theme.colors.neutralW80} />
        </XButtonWrapper>

        <ModalWrapper $isMobile={isMobile} error={error}>
          {error && (
            <Banner
              variant="error"
              mode="dark"
              style={{
                marginTop: 32,
                marginBottom: 16,
                maxWidth: '100%',
                minHeight: isMobile ? 'auto' : ''
              }}
              bannerContent={{
                title: t('invite-connection.banner-error'),
                description: t('invite-connection.banner-desc')
              }} />
          )}

          <ListTitleWrapper $isMobile={isMobile}>
            <Tonality.UIHeading
              headingStyle="headline5"
              color={theme.colors.neutralW90}
              style={{ marginBottom: 8 }}>
              {t('invite-connection.invite-title')}
            </Tonality.UIHeading>
            <Tonality.UIText textStyle="label" color={theme.colors.neutralW70}>
              {t('invite-connection.invite-list-desc')}
            </Tonality.UIText>
          </ListTitleWrapper>

          <InputWrapper $isMobile={isMobile}>
            <TextFieldDropdownWrapper $isMobile={isMobile}>
              <Tonality.TextFieldDropdown
                required
                id="emaildropdown"
                label={t('invite-connection.email')}
                setting="player"
                width="100%"
                textfieldWidth={isMobile ? '60%' : '65%'}
                placeholder={t('invite-connection.enteremail')}
                item={t('invite-connection.type')}
                listItems={options}
                error={errorsState}
                errorMessage={
                  errorsState.textField &&
                  errorsState.dropDown &&
                  !newEntry.textFieldValue &&
                  !newEntry.dropDownValue
                    ? t('invite-connection.no-input-error-message')
                    : errorsState.textField || errorsState.dropDown || ''
                }
                inputStyle={{ backgroundColor: theme.colors.neutralW20 }}
                value={{
                  textFieldValue: newEntry.textFieldValue,
                  dropDownValue: newEntry.dropDownValue
                }}
                onChange={(data) => {
                  handleFieldChange('textFieldValue', data.textFieldValue)
                  handleFieldChange('dropDownValue', data.dropDownValue)
                }} />
            </TextFieldDropdownWrapper>
          </InputWrapper>

          <ButtonWrapper>
            <Button
              variant="primary"
              setting="player"
              mode="dark"
              width="100%"
              onClick={handleInvite}>
              {error
                ? t('invite-connection.retry')
                : t('invite-connection.invite')}
            </Button>
          </ButtonWrapper>
        </ModalWrapper>
      </Modal>

      {showInviteListStateModal && <InviteState isCancel={isCancel} />}
    </>
  )
}

export default InviteList
